import { cryptoCoinInfoFetcherV2 } from '@/libs/fetcher/presets'

/**
 * Upstream response shape, verified against
 * GET https://crypto-coin-info-api.crypto.com/api/v2/equity/assets
 * on 2026-09-08:
 * - `{ ok: boolean, assets: [...], pagination: { has_more, next_page_token } }` — cursor
 *   based pagination, no total field. Asset entries match the shape previously
 *   documented for the most_traded screener (same fields observed on both
 *   endpoints).
 * - `icon_urls` is an array of `{ style, url }`, not an object. The first entry's `url`
 *   is the circular icon.
 * - `market_cap.amount` is a USD string (e.g. "5242955000000.0").
 * - `performance` is a percentage number as a string (e.g. "1.14" means +1.14%).
 */
export interface EquityScreenerAsset {
  asset_id: string // e.g. 'S_NVDA.O'
  display_symbol: string // e.g. 'NVDA'
  name: string // e.g. 'NVIDIA Corporation'
  icon_urls?: { style?: string; url?: string }[] | null
  market_cap?: { amount?: string; currency?: string } | null
  performance?: string | null // 24h change as a percentage number string
  traded_total_value?: { amount?: string; currency?: string } | null
  traded_quantity?: string | null
}

export interface EquityScreenerPagination {
  has_more: boolean
  next_page_token: string | null
}

export interface FetchMostTradedStocksResult {
  data: EquityScreenerAsset[]
  pagination: EquityScreenerPagination
}

// v2 (infinite scroll): 20 rows per page, aligned with cdc-web-main stock tab.
export const STOCKS_PAGE_SIZE = 20

/**
 * Server-side sortable fields accepted by the public assets endpoint
 * (verified 2026-09-08). 24h volume sorting is NOT exposed on the public API —
 * volume keys are silently ignored upstream.
 */
export type EquityAssetSortBy = 'market_cap' | 'performance' | 'alphabetical'

export interface EquityAssetSort {
  sortBy: EquityAssetSortBy
  sortDirection: 'asc' | 'desc'
}

interface MostTradedStocksResponse {
  ok?: boolean
  assets?: EquityScreenerAsset[]
  pagination?: {
    has_more?: boolean
    next_page_token?: string | null
  }
}

/**
 * Fetches the public equity asset list (`/api/v2/equity/assets`).
 *
 * Without a `sort` the upstream returns its default most-traded-first order,
 * which matches the previous most_traded screener behavior. With a `sort` the
 * upstream reorders server-side; pagination cursors are only valid within the
 * same sort order, so callers must reset paging state when the sort changes.
 */
export async function fetchMostTradedStocks({
  pageToken,
  pageSize = STOCKS_PAGE_SIZE,
  sort,
  abortController,
}: {
  pageToken?: string
  pageSize?: number
  /** Omit to use the upstream default (most-traded-like) order. */
  sort?: EquityAssetSort
  /** Pass to cancel the in-flight request when the caller unmounts. */
  abortController?: AbortController
}): Promise<FetchMostTradedStocksResult> {
  const response = await cryptoCoinInfoFetcherV2<MostTradedStocksResponse>(
    '/equity/assets',
    {
      method: 'GET',
      // Disable the Next.js server fetch cache: freshness is handled by the
      // caller (React Query or hand-rolled retry in useCursorPagedStocks).
      cache: 'no-store',
      queries: {
        page_size: pageSize,
        ...(pageToken ? { page_token: pageToken } : {}),
        ...(sort ? { sort_by: sort.sortBy, sort_direction: sort.sortDirection } : {}),
      },
    },
    abortController ? { abortController } : undefined,
  )

  return {
    data: response?.assets ?? [],
    pagination: {
      has_more: response?.pagination?.has_more ?? false,
      next_page_token: response?.pagination?.next_page_token ?? null,
    },
  }
}
